import React, { useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Database, CheckCircle2, AlertTriangle, ShieldCheck,
  Gauge, Cpu, Images as ImagesIcon, SlidersHorizontal, Lock, Server,
  Scale, Trophy, RotateCcw, Power, FlaskConical, Play, XCircle, Sparkles,
} from 'lucide-react';
import {
  getHdrDatasetOverview,
  syncHdrTrainingPairs,
  listHdrTrainingPairs,
  setHdrPairRights,
  getHdrEngineRunsOverview,
  getHdrEngineJobsOverview,
  getHdrPairDetail,
  getHdrBenchmarkOverview,
  getHdrBenchmarkNext,
  submitHdrBenchmarkVote,
  getHdrBenchmarkResults,
  getHdrRollout,
  setHdrRollout,
  rollbackHdrRollout,
  enqueueHdrSandbox,
  preferHdrSandboxCandidate,
  getHdrEngineJobDetail,
  hdrStorageUrl,
  type HdrDatasetOverview,
  type HdrTrainingPair,
  type HdrEngineRunsOverview,
  type HdrEngineJobsOverview,
  type HdrPairDetail,
  type HdrBenchmarkOverview,
  type HdrBenchmarkNext,
  type HdrBenchmarkResults,
  type HdrBenchmarkAggregate,
  type HdrBenchmarkPreference,
  type HdrRolloutState,
  type HdrRolloutMode,
  type HdrSandboxJobDetail,
  type HdrFinishChecks,
  type HdrProviderMeta,
  type HdrPreferenceChoice,
} from '../services/hdrEngine';
import { getHdrAdminPipeline, getThumbnailUrl, type HdrPipelineSetting } from '../services/supabase';

const PROVIDER_LABELS: Record<string, string> = {
  autoenhance: 'Autoenhance (AE)',
  autopix: 'Autopix (AP)',
  gemini: 'ReHub / Gemini (internal)',
  fal: 'ReHub / fal (internal)',
  internal: 'ReHub engine (internal)',
  none: 'None',
  unknown: 'Unknown',
};

const VALIDATION_STYLES: Record<string, string> = {
  valid: 'bg-emerald-500/20 text-emerald-300',
  pending: 'bg-slate-700 text-slate-300',
  quarantined: 'bg-amber-500/20 text-amber-300',
  rejected: 'bg-red-500/20 text-red-300',
};

const RIGHTS_STYLES: Record<string, string> = {
  approved: 'bg-emerald-500/20 text-emerald-300',
  needs_review: 'bg-amber-500/20 text-amber-300',
  restricted: 'bg-red-500/20 text-red-300',
};

const JOB_STATUS_STYLES: Record<string, string> = {
  queued: 'bg-slate-700 text-slate-300',
  processing: 'bg-blue-500/20 text-blue-300',
  completed: 'bg-emerald-500/20 text-emerald-300',
  failed: 'bg-red-500/20 text-red-300',
};

const AE_BASELINE_USD = 0.4; // what an external AE merge would have cost per image

type SubTab = 'overview' | 'datasets' | 'models' | 'compare' | 'sandbox' | 'benchmarks' | 'config';

const SUB_TABS: { id: SubTab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <Gauge size={15} /> },
  { id: 'datasets', label: 'Training datasets', icon: <Database size={15} /> },
  { id: 'models', label: 'Model management', icon: <Cpu size={15} /> },
  { id: 'compare', label: 'Image comparison', icon: <ImagesIcon size={15} /> },
  { id: 'sandbox', label: 'Sandbox', icon: <FlaskConical size={15} /> },
  { id: 'benchmarks', label: 'Benchmarks', icon: <Scale size={15} /> },
  { id: 'config', label: 'Processing configuration', icon: <SlidersHorizontal size={15} /> },
];

const HdrEngineAdmin: React.FC = () => {
  const [sub, setSub] = useState<SubTab>('overview');
  return (
    <div className="space-y-5 max-w-6xl">
      <nav className="flex flex-wrap gap-1.5">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm transition-colors ${
              sub === t.id ? 'bg-brand-600 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </nav>

      {sub === 'overview' && <OverviewPanel />}
      {sub === 'datasets' && <TrainingDatasetsPanel />}
      {sub === 'models' && <ModelManagementPanel />}
      {sub === 'compare' && <ImageComparePanel />}
      {sub === 'sandbox' && <SandboxPanel />}
      {sub === 'benchmarks' && <BenchmarksPanel />}
      {sub === 'config' && <ProcessingConfigPanel />}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OVERVIEW — engine processing outcomes + job queue (Phase 6/7 run history)
// ─────────────────────────────────────────────────────────────────────────────
const OverviewPanel: React.FC = () => {
  const [runs, setRuns] = useState<HdrEngineRunsOverview | null>(null);
  const [jobs, setJobs] = useState<HdrEngineJobsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [r, j] = await Promise.all([getHdrEngineRunsOverview(50), getHdrEngineJobsOverview(25)]);
    if (!r.success) setError(r.error || 'Failed to load engine run history');
    else setRuns(r.overview || null);
    if (j.success) setJobs(j.overview || null);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const t = runs?.totals;
  const n = t?.runs || 0;
  const pct = (x: number) => (n > 0 ? Math.round((x / n) * 100) : 0);
  const aeDelivered = runs?.by_delivered_source?.autoenhance || 0;
  const apDelivered = runs?.by_delivered_source?.autopix || 0;
  const savings = n > 0 ? n * AE_BASELINE_USD - (t?.total_cost_usd || 0) : 0;
  const qualityRate = t && t.quality_graded > 0 ? Math.round((t.quality_pass / t.quality_graded) * 100) : null;

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Processing outcomes"
        subtitle="What the internal engine decided for each processed image. Offline until rollout (Phase 10) — these are audit runs, not production deliveries."
        onRefresh={load}
        loading={loading}
      />
      {error && <ErrorBox error={error} />}

      {loading && !runs ? (
        <Spinner />
      ) : n === 0 ? (
        <EmptyBox>
          No engine runs recorded yet. Run <code>rehub-hdr-engine process --record</code> or enqueue work for the
          durable worker (<code>rehub-hdr-engine enqueue</code> → <code>work</code>) to populate this history.
        </EmptyBox>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Photographs processed" value={n} />
            <Stat label="Internal ReHub" value={`${pct(t?.delivered_internal || 0)}%`} accent="text-emerald-300"
              sub={`${t?.delivered_internal || 0} delivered`} />
            <Stat label="AE fallback" value={`${pct(aeDelivered)}%`} accent="text-sky-300" sub={`${aeDelivered} images`} />
            <Stat label="AP fallback" value={`${pct(apDelivered)}%`} accent="text-sky-300" sub={`${apDelivered} images`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Avg processing time" value={`${((t?.avg_duration_ms || 0) / 1000).toFixed(1)}s`} />
            <Stat label="Avg processing cost" value={`$${(t?.avg_cost_usd || 0).toFixed(3)}`} />
            <Stat label="Est. savings vs AE" value={`$${savings.toFixed(2)}`} accent={savings >= 0 ? 'text-emerald-300' : 'text-red-300'}
              sub={`vs $${AE_BASELINE_USD.toFixed(2)}/img external`} />
            <Stat label="Quality pass rate" value={qualityRate === null ? '—' : `${qualityRate}%`}
              sub={qualityRate === null ? 'no graded runs' : `${t?.quality_pass}/${t?.quality_graded} graded`} />
          </div>

          {/* Manual review callout */}
          {(t?.manual_review || 0) > 0 && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{t?.manual_review} run{t?.manual_review === 1 ? '' : 's'} escalated to manual review</p>
                <p className="text-amber-300/80 text-xs mt-0.5">Neither the internal engine nor a fallback provider produced a confident result.</p>
              </div>
            </div>
          )}

          {/* Engine job queue (Phase 7) */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Server size={15} className="text-brand-400" /> Engine job queue</h3>
            {jobs && Object.keys(jobs.by_status || {}).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {Object.entries(jobs.by_status).map(([status, count]) => (
                  <span key={status} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium ${JOB_STATUS_STYLES[status] || 'bg-slate-800 text-slate-300'}`}>
                    {status}: {count}
                  </span>
                ))}
                {jobs.oldest_queued_at && (
                  <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs text-slate-500">
                    oldest queued {new Date(jobs.oldest_queued_at).toLocaleString()}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Queue empty — the isolated engine queue is idle (nothing enqueues it in production).</p>
            )}
          </div>

          {/* Recent runs */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800"><h3 className="text-sm font-semibold text-white">Recent runs</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                    <th className="px-3 py-2.5 font-medium">Decision</th>
                    <th className="px-3 py-2.5 font-medium">Delivered by</th>
                    <th className="px-3 py-2.5 font-medium">Attempts</th>
                    <th className="px-3 py-2.5 font-medium">Providers called</th>
                    <th className="px-3 py-2.5 font-medium">Cost</th>
                    <th className="px-3 py-2.5 font-medium">Time</th>
                    <th className="px-3 py-2.5 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {runs?.recent.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                      <td className="px-3 py-2.5"><ActionBadge action={r.final_action} /></td>
                      <td className="px-3 py-2.5 text-slate-400">{PROVIDER_LABELS[r.delivered_source || 'none'] || r.delivered_source || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-400">{r.attempts}{r.retry_count ? ` (${r.retry_count} retry)` : ''}</td>
                      <td className="px-3 py-2.5 text-slate-500">{(r.providers_called || []).join(', ') || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-400">${Number(r.est_cost_usd || 0).toFixed(3)}</td>
                      <td className="px-3 py-2.5 text-slate-500">{r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function ActionBadge({ action }: { action: string }) {
  const map: Record<string, string> = {
    deliver_internal: 'bg-emerald-500/20 text-emerald-300',
    deliver_fallback: 'bg-sky-500/20 text-sky-300',
    manual_review: 'bg-amber-500/20 text-amber-300',
  };
  const label: Record<string, string> = {
    deliver_internal: 'Internal',
    deliver_fallback: 'Fallback',
    manual_review: 'Manual review',
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[action] || 'bg-slate-700 text-slate-300'}`}>{label[action] || action}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// MODEL MANAGEMENT — active model versions (filesystem registry is authoritative)
// ─────────────────────────────────────────────────────────────────────────────
const ModelManagementPanel: React.FC = () => {
  const [runs, setRuns] = useState<HdrEngineRunsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const r = await getHdrEngineRunsOverview(1);
    if (!r.success) setError(r.error || 'Failed to load model versions');
    else setRuns(r.overview || null);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const nets = ['tone', 'colour', 'windows', 'quality'] as const;
  const versions = runs?.model_versions_latest || {};

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Model management"
        subtitle="The four constrained nets (ToneNet / ColourNet / WindowNet / QualityNet). The authoritative registry is the engine's filesystem model_registry (promote / rollback via the CLI); the versions below are those recorded on the most recent engine run."
        onRefresh={load}
        loading={loading}
      />
      {error && <ErrorBox error={error} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {nets.map((net) => (
          <div key={net} className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
            <p className="text-xs text-slate-500 capitalize">{net}Net</p>
            <p className="text-lg font-semibold text-white mt-1">
              {versions[net] ? `v${versions[net]}` : <span className="text-slate-500">classical</span>}
            </p>
            <p className="text-[11px] text-slate-600 mt-1">{versions[net] ? 'learned weights active' : 'constrained classical path'}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-3 text-sm text-slate-400">
        <h3 className="text-sm font-semibold text-white">Training history &amp; evaluation</h3>
        <p>
          Training runs, checkpoints, metrics and held-out evaluation reports are versioned in the engine's filesystem
          registry under <code className="text-slate-300">models/&lt;net&gt;/</code> (<code>registry.json</code> + TorchScript
          <code>.ptl</code> checkpoints, plus <code>eval_*.json</code>). Manage them with the engine CLI:
        </p>
        <ul className="list-disc list-inside space-y-1 text-slate-500">
          <li><code>python -m rehub_hdr_engine.training.train_tonenet</code> (and <code>train_colournet</code> / <code>train_windownet</code>) — train + register a new version</li>
          <li><code>python -m rehub_hdr_engine.training.evaluate</code> — PSNR / SSIM / mask-IoU on the held-out test split</li>
          <li>registry <code>set_active</code> / <code>rollback</code> — promote or revert the active checkpoint</li>
        </ul>
        <p className="text-[11px] text-slate-600">
          Model files are never served publicly. A learned net only ever predicts the same low-dimensional constrained
          parameters its classical path uses, so a model can never do unconstrained pixel replacement.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE COMPARISON — brackets vs historical provider output (ReHub via Phase 9)
// ─────────────────────────────────────────────────────────────────────────────
const ImageComparePanel: React.FC = () => {
  const [pairs, setPairs] = useState<HdrTrainingPair[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<HdrPairDetail | null>(null);
  const [providerFilter, setProviderFilter] = useState('');
  const [mode, setMode] = useState<'side' | 'slider'>('side');
  const [slider, setSlider] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPairs = async () => {
    setLoading(true);
    setError(null);
    const res = await listHdrTrainingPairs({ limit: 100, validation: 'valid', provider: providerFilter || null });
    if (!res.success) setError(res.error || 'Failed to load pairs');
    else {
      setPairs(res.pairs || []);
      if (res.pairs && res.pairs.length && !res.pairs.find((p) => p.id === selectedId)) {
        setSelectedId(res.pairs[0].id);
      }
    }
    setLoading(false);
  };

  const loadDetail = async (id: string) => {
    if (!id) { setDetail(null); return; }
    const res = await getHdrPairDetail(id);
    if (res.success) setDetail(res.pair || null);
    else { setError(res.error || 'Failed to load pair detail'); setDetail(null); }
  };

  useEffect(() => { loadPairs(); /* eslint-disable-next-line */ }, [providerFilter]);
  useEffect(() => { loadDetail(selectedId); /* eslint-disable-next-line */ }, [selectedId]);

  const brackets = detail?.source_storage_paths || [];
  const middle = brackets.length ? brackets[Math.floor(brackets.length / 2)] : null;
  const outputUrl = hdrStorageUrl(detail?.output_storage_path);
  const beforeUrl = hdrStorageUrl(middle);

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Image comparison"
        subtitle="Source brackets vs the historical provider output for a training pair. The ReHub engine column is produced during blind benchmarking (Phase 9)."
        onRefresh={loadPairs}
        loading={loading}
      />
      {error && <ErrorBox error={error} />}

      <div className="flex flex-wrap items-center gap-2">
        <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
          <option value="">All providers</option>
          <option value="autoenhance">Autoenhance</option>
          <option value="autopix">Autopix</option>
          <option value="gemini">Gemini</option>
          <option value="fal">fal</option>
        </select>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="flex-1 min-w-[220px] bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
          {pairs.length === 0 && <option value="">No valid pairs</option>}
          {pairs.map((p) => (
            <option key={p.id} value={p.id}>{p.listing_name || p.media_item_id} · {p.provider} · {p.source_count} brackets</option>
          ))}
        </select>
        <div className="flex rounded-lg overflow-hidden border border-slate-700">
          <button onClick={() => setMode('side')} className={`px-3 py-1.5 text-xs ${mode === 'side' ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'}`}>Side by side</button>
          <button onClick={() => setMode('slider')} className={`px-3 py-1.5 text-xs ${mode === 'slider' ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-400'}`}>Before / after</button>
        </div>
      </div>

      {!detail ? (
        <EmptyBox>Select a training pair to compare.</EmptyBox>
      ) : mode === 'slider' ? (
        beforeUrl && outputUrl ? (
          <div className="space-y-3">
            <div className="relative w-full max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-800 select-none" style={{ aspectRatio: '4 / 3' }}>
              <img src={getThumbnailUrl(beforeUrl, { width: 1600, height: 1600, quality: 72, resize: 'contain' }) || beforeUrl} alt="brackets" className="absolute inset-0 w-full h-full object-contain bg-black" />
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${slider}%` }}>
                <img src={getThumbnailUrl(outputUrl, { width: 1600, height: 1600, quality: 72, resize: 'contain' }) || outputUrl} alt="output" className="absolute inset-0 h-full object-contain bg-black" style={{ width: '100%', maxWidth: 'none' }} />
              </div>
              <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-slate-200">Mid bracket</span>
              <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-brand-200">{PROVIDER_LABELS[detail.provider] || detail.provider}</span>
            </div>
            <input type="range" min={0} max={100} value={slider} onChange={(e) => setSlider(Number(e.target.value))} className="w-full max-w-3xl mx-auto block accent-brand-500" />
          </div>
        ) : (
          <EmptyBox>This pair is missing a bracket or output image.</EmptyBox>
        )
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-slate-500 mb-2">Source brackets ({brackets.length})</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {brackets.map((path, i) => {
                const raw = /\.(cr2|cr3|nef|nrw|arw|dng|raf|orf|rw2|pef|srw)$/i.test(path);
                const u = raw ? null : hdrStorageUrl(path);
                return (
                  <a key={i} href={hdrStorageUrl(path) || undefined} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-slate-800 bg-black">
                    {u ? (
                      <img src={getThumbnailUrl(u, { width: 640, height: 640, quality: 65, resize: 'contain' }) || u} alt={`bracket ${i + 1}`} className="w-full aspect-[4/3] object-contain bg-black" loading="lazy" onError={(e) => { if (e.currentTarget.src !== u) e.currentTarget.src = u; }} />
                    ) : (
                      <div className="w-full aspect-[4/3] flex items-center justify-center text-[11px] text-slate-500">Bracket {i + 1} · RAW</div>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <CompareCard title={PROVIDER_LABELS[detail.provider] || detail.provider} subtitle="historical output" url={outputUrl} dims={detail.output_width && detail.output_height ? `${detail.output_width}×${detail.output_height}` : null} />
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 p-5 flex flex-col items-center justify-center text-center">
              <Cpu size={22} className="text-slate-600 mb-2" />
              <p className="text-sm font-medium text-slate-300">ReHub engine output</p>
              <p className="text-xs text-slate-500 mt-1">Generated during blind benchmarking (Phase 9). Run the engine on this pair to produce a comparable result.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function CompareCard({ title, subtitle, url, dims, footer }: { title: string; subtitle: string; url: string | null; dims: string | null; footer?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 overflow-hidden flex flex-col">
      <div className="px-3 border-b border-slate-800 h-14 shrink-0 flex flex-col justify-center">
        <p className="text-sm font-medium text-white leading-tight truncate">{title}</p>
        <p className="text-[11px] text-slate-500 leading-tight truncate">{subtitle}{dims ? ` · ${dims}` : ''}</p>
      </div>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="block bg-black">
          <img src={getThumbnailUrl(url, { width: 1600, height: 1600, quality: 78, resize: 'contain' }) || url} alt={title} className="w-full aspect-[3/2] object-contain bg-black" loading="lazy" onError={(e) => { if (url && e.currentTarget.src !== url) e.currentTarget.src = url; }} />
        </a>
      ) : (
        <div className="aspect-[3/2] flex items-center justify-center text-sm text-slate-600">No image</div>
      )}
      {footer && <div className="px-3 py-2 border-t border-slate-800 text-[11px] text-slate-400 space-y-1">{footer}</div>}
    </div>
  );
}

const CHECK_LABELS: Record<string, string> = {
  dull_interior: 'dull interior',
  colour_cast: 'colour cast',
  colour_patches: 'colour patches',
  washed_exterior: 'washed exterior',
  texture_loss: 'texture loss',
  window_speckle: 'window speckle',
  window_blotch: 'window blotch',
};

/** QualityNet and the finish checks for one output, each labelled with the profile it was measured on. */
function FinishFooter({ profile, quality, checks }: { profile: string; quality?: Record<string, any> | null; checks?: HdrFinishChecks | null }) {
  if (!quality && !checks) return null;
  return (
    <>
      {quality?.verdict && (
        <p>
          QualityNet ({profile}): <span className="text-slate-200">{String(quality.verdict)}</span>
          {typeof quality.score === 'number' ? ` · ${quality.score.toFixed(2)}` : ''}
        </p>
      )}
      {checks && (
        <p>
          Checks ({checks.profile || profile}): wall L {checks.wall_l ?? '—'}
          {checks.texture_vs_merge != null ? ` · texture ${checks.texture_vs_merge.toFixed(2)}× merge` : ''}
          {' · '}
          {checks.flags.length
            ? <span className="text-amber-300">{checks.flags.map((f) => CHECK_LABELS[f] || f).join(', ')}</span>
            : <span className="text-emerald-300">no flags</span>}
        </p>
      )}
    </>
  );
}

function ProviderFooter({ meta }: { meta?: HdrProviderMeta | null }) {
  if (!meta) return null;
  const guard = meta.sky_guard;
  return (
    <>
      <p>Given the ReHub <span className="text-slate-200">{meta.input_profile || 'baseline'}</span> render.</p>
      {guard && (
        <p>
          Sky guard: <span className={guard.decision === 'applied' ? 'text-emerald-300' : 'text-amber-300'}>{guard.decision}</span>
          {' · '}{guard.reason}
          {typeof guard.provider_sky_outside_mask === 'number' && guard.provider_sky_outside_mask > 0
            ? ` · provider painted sky on ${(guard.provider_sky_outside_mask * 100).toFixed(1)}% of the frame outside confirmed sky`
            : ''}
        </p>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SANDBOX — run the engine on one picked image and three-way compare the result
// ─────────────────────────────────────────────────────────────────────────────
const ACTION_LABELS: Record<string, { label: string; style: string }> = {
  deliver_internal: { label: 'ReHub engine (internal)', style: 'bg-emerald-500/20 text-emerald-300' },
  deliver_fallback: { label: 'Fallback provider', style: 'bg-sky-500/20 text-sky-300' },
  manual_review: { label: 'Manual review', style: 'bg-amber-500/20 text-amber-300' },
};
const QUALITY_STYLES: Record<string, string> = {
  pass: 'bg-emerald-500/20 text-emerald-300',
  review: 'bg-amber-500/20 text-amber-300',
  fail: 'bg-red-500/20 text-red-300',
};

// Mirrors rehub_hdr_engine/fal_refine.py FAL_MODELS. One fal model runs per test.
const FAL_MODEL_OPTIONS: { id: string; label: string; price: string; prompted: boolean }[] = [
  { id: 'fal-ai/flux-2/edit', label: 'FLUX.2 Dev Edit', price: '~$0.07, source size', prompted: true },
  { id: 'fal-ai/qwen-image-edit-2511', label: 'Qwen Image Edit 2511', price: '~$0.60 at source size', prompted: true },
  { id: 'fal-ai/flux-kontext/dev', label: 'FLUX Kontext Dev', price: '~$0.05, source size', prompted: true },
  { id: 'fal-ai/image-editing/color-correction', label: 'Colour correction', price: '~$0.06, source size', prompted: false },
  { id: 'fal-ai/image-apps-v2/relighting', label: 'Relighting (natural)', price: '~$0.06, source size', prompted: false },
];

const SandboxPanel: React.FC = () => {
  const [pairs, setPairs] = useState<HdrTrainingPair[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [detail, setDetail] = useState<HdrSandboxJobDetail | null>(null);
  const [enqueuing, setEnqueuing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [useGemini, setUseGemini] = useState(false);
  const [useOpenai, setUseOpenai] = useState(false);
  const [useFal, setUseFal] = useState(false);
  const [falModel, setFalModel] = useState(FAL_MODEL_OPTIONS[0].id);
  const [replaceSky, setReplaceSky] = useState(true);
  const [providerBase, setProviderBase] = useState<'baseline' | 'bright-2'>('baseline');
  const [preferring, setPreferring] = useState(false);

  const loadPairs = async () => {
    setLoading(true);
    setError(null);
    const res = await listHdrTrainingPairs({ limit: 100, validation: 'valid', provider: providerFilter || null });
    if (!res.success) setError(res.error || 'Failed to load pairs');
    else {
      setPairs(res.pairs || []);
      if (res.pairs && res.pairs.length && !res.pairs.find((p) => p.id === selectedId)) {
        setSelectedId(res.pairs[0].id);
      }
    }
    setLoading(false);
  };
  useEffect(() => { loadPairs(); /* eslint-disable-next-line */ }, [providerFilter]);

  const selectedPair = pairs.find((p) => p.id === selectedId) || null;
  const selectedPreviewUrl = hdrStorageUrl(selectedPair?.output_storage_path || null);
  const status = detail?.job.status;
  const running = jobId && (!status || status === 'queued' || status === 'processing');

  // Poll the job detail while the job is running.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      const res = await getHdrEngineJobDetail(jobId);
      if (cancelled) return;
      if (res.success && res.detail) setDetail(res.detail);
      else if (!res.success) setError(res.error || 'Failed to load job detail');
    };
    tick();
    const poll = setInterval(() => {
      setDetail((d) => {
        const s = d?.job.status;
        if (s === 'completed' || s === 'failed') return d; // stop updating via interval
        tick();
        return d;
      });
    }, 2500);
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { cancelled = true; clearInterval(poll); clearInterval(timer); };
  }, [jobId]);

  // Stop the clock once terminal.
  useEffect(() => {
    if (status === 'completed' || status === 'failed') setElapsed((e) => e);
  }, [status]);

  const run = async () => {
    if (!selectedPair) return;
    setEnqueuing(true);
    setError(null);
    setDetail(null);
    setElapsed(0);
    const res = await enqueueHdrSandbox({
      mediaItemId: selectedPair.media_item_id,
      gemini: useGemini,
      geminiSky: useGemini && replaceSky,
      openai: useOpenai,
      openaiSky: useOpenai && replaceSky,
      fal: useFal,
      falSky: useFal && replaceSky,
      falModel: useFal ? falModel : null,
      providerBase,
    });
    setEnqueuing(false);
    if (!res.success || !res.jobId) { setError(res.error || 'Failed to enqueue sandbox job'); return; }
    setJobId(res.jobId);
  };

  const previewBrackets = detail?.job.sandbox_bracket_paths;
  const brackets = (previewBrackets && previewBrackets.length)
    ? previewBrackets
    : (detail?.source?.source_storage_paths || []);
  const rehubUrl = hdrStorageUrl(detail?.job.result_image_path);
  const listed = detail?.job.result_candidates || {};
  const candidateUrl = hdrStorageUrl(listed['bright-1']?.path || detail?.job.result_image_candidate_path);
  const candidateMeta = listed['bright-1'] || detail?.job.result_candidate_meta || null;
  const bright2Url = hdrStorageUrl(listed['bright-2']?.path || null);
  const bright2Meta = listed['bright-2'] || null;
  const geminiUrl = hdrStorageUrl(detail?.job.result_image_gemini_path);
  const geminiRequested = Boolean(detail?.job?.result_image_gemini_path) || useGemini;
  const openaiUrl = hdrStorageUrl(detail?.job.result_image_openai_path);
  const openaiRequested = Boolean(detail?.job?.result_image_openai_path) || useOpenai;
  const falUrl = hdrStorageUrl(detail?.job.result_image_fal_path);
  const falRequested = Boolean(detail?.job?.result_image_fal_path) || useFal;
  const falOption = FAL_MODEL_OPTIONS.find((m) => m.id === falModel) || FAL_MODEL_OPTIONS[0];
  const historicalUrl = hdrStorageUrl(detail?.source?.output_storage_path);
  const showCandidate = Boolean(candidateUrl) || Boolean(running);
  const showBright2 = Boolean(bright2Url) || Boolean(running);
  const compareCols = 1 + (showCandidate ? 1 : 0) + (showBright2 ? 1 : 0) + (geminiRequested ? 1 : 0) + (openaiRequested ? 1 : 0) + (falRequested ? 1 : 0) + 1;
  const compareGridClass =
    compareCols >= 4 ? 'sm:grid-cols-2 xl:grid-cols-3'
    : compareCols === 3 ? 'sm:grid-cols-2 lg:grid-cols-3'
    : 'sm:grid-cols-2';
  const shownChoices = [
    'baseline',
    ...(showCandidate ? ['bright'] : []),
    ...(showBright2 ? ['bright-2'] : []),
    'historical',
    ...(geminiRequested ? ['gemini'] : []),
    ...(openaiRequested ? ['openai'] : []),
  ];
  const savePreference = async (choice: 'baseline' | 'bright' | 'bright-2' | 'historical' | 'gemini' | 'openai' | 'tie') => {
    if (!jobId) return;
    setPreferring(true);
    const res = await preferHdrSandboxCandidate(jobId, choice, shownChoices);
    setPreferring(false);
    if (!res.success) { setError(res.error || 'Could not save that choice'); return; }
    setDetail((d) => d ? { ...d, job: { ...d.job, candidate_preference: choice } } : d);
  };
  const quality = detail?.run?.quality || null;
  const verdict = (quality?.verdict as string) || null;
  const metrics = (quality?.metrics as Record<string, number>) || null;
  const action = detail?.run?.final_action;

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Model sandbox"
        subtitle="Run the internal ReHub engine on one archive image, then compare it side-by-side with the source brackets, optional Gemini and OpenAI generative passes, and the historical provider output. Isolated queue — nothing is delivered to users or production."
        onRefresh={loadPairs}
        loading={loading}
      />
      {error && <ErrorBox error={error} />}

      <div className="flex flex-wrap items-center gap-2">
        <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
          <option value="">All providers</option>
          <option value="autoenhance">Autoenhance</option>
          <option value="autopix">Autopix</option>
          <option value="gemini">Gemini</option>
          <option value="fal">fal</option>
        </select>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="flex-1 min-w-[220px] bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
          {pairs.length === 0 && <option value="">No valid pairs</option>}
          {pairs.map((p) => (
            <option key={p.id} value={p.id}>{p.listing_name || p.media_item_id} · {p.provider} · {p.source_count} brackets</option>
          ))}
        </select>
        <button
          onClick={run}
          disabled={!selectedPair || !!running || enqueuing}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {enqueuing || running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
          Run in sandbox
        </button>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={useGemini}
            onChange={(e) => setUseGemini(e.target.checked)}
            disabled={!!running || enqueuing}
            className="rounded border-slate-600 bg-slate-800 text-brand-500 focus:ring-brand-500"
          />
          <Sparkles size={13} className="text-fuchsia-400" />
          Gemini (~$0.04)
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={useOpenai}
            onChange={(e) => setUseOpenai(e.target.checked)}
            disabled={!!running || enqueuing}
            className="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
          />
          <Sparkles size={13} className="text-emerald-400" />
          OpenAI gpt-image-2.5 (~$0.38, 4K)
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={useFal}
            onChange={(e) => setUseFal(e.target.checked)}
            disabled={!!running || enqueuing}
            className="rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500"
          />
          <Sparkles size={13} className="text-violet-400" />
          fal model
        </label>
        {useFal && (
          <select
            value={falModel}
            onChange={(e) => setFalModel(e.target.value)}
            disabled={!!running || enqueuing}
            className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5"
          >
            {FAL_MODEL_OPTIONS.map((m) => (
              <option key={m.id} value={m.id}>{m.label} ({m.price})</option>
            ))}
          </select>
        )}
        {(useGemini || useOpenai || useFal) && (
          <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={replaceSky}
              onChange={(e) => setReplaceSky(e.target.checked)}
              disabled={!!running || enqueuing}
              className="rounded border-slate-600 bg-slate-800 text-sky-500 focus:ring-sky-500"
            />
            Replace dull sky with blue (like AE)
          </label>
        )}
        {(useGemini || useOpenai || useFal) && (
          <label className="inline-flex items-center gap-2 text-xs text-slate-400">
            Give provider
            <select
              value={providerBase}
              onChange={(e) => setProviderBase(e.target.value as 'baseline' | 'bright-2')}
              disabled={!!running || enqueuing}
              className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1"
            >
              <option value="baseline">the baseline</option>
              <option value="bright-2">bright-2</option>
            </select>
          </label>
        )}
      </div>

      {/* Selected scene preview — so you can see what you're about to test */}
      {selectedPair && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="flex items-start gap-4">
            <a
              href={selectedPreviewUrl || undefined}
              target="_blank"
              rel="noreferrer"
              className="block w-44 shrink-0 rounded-lg overflow-hidden border border-slate-800 bg-black"
            >
              {selectedPreviewUrl ? (
                <img
                  src={getThumbnailUrl(selectedPreviewUrl, { width: 480, height: 360, quality: 70, resize: 'contain' }) || selectedPreviewUrl}
                  alt="selected scene"
                  className="w-full aspect-[4/3] object-contain bg-black"
                  loading="lazy"
                  onError={(e) => { if (selectedPreviewUrl && e.currentTarget.src !== selectedPreviewUrl) e.currentTarget.src = selectedPreviewUrl; }}
                />
              ) : (
                <div className="w-full aspect-[4/3] flex items-center justify-center text-xs text-slate-600">No preview</div>
              )}
            </a>
            <div className="min-w-0 text-sm">
              <p className="font-medium text-slate-200 truncate">{selectedPair.listing_name || selectedPair.media_item_id}</p>
              <p className="text-xs text-slate-400 mt-1">
                {(PROVIDER_LABELS[selectedPair.provider] || selectedPair.provider)} · {selectedPair.source_count} brackets
                {selectedPair.source_width && selectedPair.source_height ? ` · ${selectedPair.source_width}×${selectedPair.source_height}` : ''}
              </p>
              <p className="text-xs text-slate-500 mt-2">Preview is the historical delivered image for this scene. The sandbox re-merges from the source brackets, so the full bracket set appears once the run starts.</p>
            </div>
          </div>
        </div>
      )}

      {/* Job status / worker hint */}
      {jobId && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${JOB_STATUS_STYLES[status || 'queued'] || 'bg-slate-800 text-slate-300'}`}>
                {running && <Loader2 size={12} className="animate-spin" />}
                {status || 'queued'}
              </span>
              <span className="text-slate-500 text-xs font-mono">job {jobId.slice(0, 8)}</span>
              {running && <span className="text-slate-500 text-xs">· {elapsed}s</span>}
            </div>
            {status === 'failed' && <span className="inline-flex items-center gap-1.5 text-xs text-red-300"><XCircle size={14} /> {detail?.job.error_message || 'Job failed'}</span>}
          </div>
          {(status === 'queued' || !status) && elapsed > 6 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                Still queued — the engine worker must be running to pick this up. Start it with{' '}
                <code>rehub-hdr-engine work</code> (locally or on Railway) draining the isolated <code>hdr_engine_jobs</code> queue.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Three-way comparison */}
      {detail && (status === 'completed' || brackets.length > 0) && (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-slate-500 mb-2">Source brackets ({brackets.length})</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {brackets.map((path, i) => {
                const raw = /\.(cr2|cr3|nef|nrw|arw|dng|raf|orf|rw2|pef|srw)$/i.test(path);
                const u = raw ? null : hdrStorageUrl(path);
                return (
                  <a key={i} href={hdrStorageUrl(path) || undefined} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-slate-800 bg-black">
                    {u ? (
                      <img src={getThumbnailUrl(u, { width: 640, height: 640, quality: 65, resize: 'contain' }) || u} alt={`bracket ${i + 1}`} className="w-full aspect-[4/3] object-contain bg-black" loading="lazy" onError={(e) => { if (e.currentTarget.src !== u) e.currentTarget.src = u; }} />
                    ) : (
                      <div className="w-full aspect-[4/3] flex items-center justify-center text-[11px] text-slate-500">Bracket {i + 1} · RAW</div>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
          <div className={`grid gap-3 ${compareGridClass}`}>
            {status === 'completed' && rehubUrl ? (
              <CompareCard
                title="ReHub engine (internal)"
                subtitle="baseline"
                url={rehubUrl}
                dims={null}
                footer={<FinishFooter profile="baseline" quality={detail?.run?.quality} checks={listed.baseline?.checks} />}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 p-5 flex flex-col items-center justify-center text-center min-h-[200px]">
                {running ? <Loader2 size={22} className="text-brand-400 mb-2 animate-spin" /> : <Cpu size={22} className="text-slate-600 mb-2" />}
                <p className="text-sm font-medium text-slate-300">ReHub engine output</p>
                <p className="text-xs text-slate-500 mt-1">{running ? 'Processing on the isolated engine queue…' : (detail?.run?.notes || 'No result image produced for this run.')}</p>
              </div>
            )}
            {showCandidate && (
              candidateUrl ? (
                <CompareCard
                  title="ReHub bright-1"
                  subtitle="sandbox candidate"
                  url={candidateUrl}
                  dims={null}
                  footer={<FinishFooter profile="bright-1" quality={candidateMeta?.quality} checks={candidateMeta?.checks} />}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 flex flex-col min-h-[200px]">
                  <div className="px-3 border-b border-slate-800 h-14 shrink-0 flex flex-col justify-center">
                    <p className="text-sm font-medium text-slate-300 leading-tight">ReHub bright-1</p>
                    <p className="text-[11px] text-slate-500 leading-tight">sandbox candidate</p>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                    {running ? <Loader2 size={22} className="text-brand-400 mb-2 animate-spin" /> : <Cpu size={22} className="text-slate-600 mb-2" />}
                    <p className="text-xs text-slate-500">{running ? 'Finishing the same merge with bright-1…' : 'No bright-1 image for this run.'}</p>
                  </div>
                </div>
              )
            )}
            {showBright2 && (
              bright2Url ? (
                <CompareCard
                  title="ReHub bright-2"
                  subtitle="sandbox candidate"
                  url={bright2Url}
                  dims={null}
                  footer={<FinishFooter profile="bright-2" quality={bright2Meta?.quality} checks={bright2Meta?.checks} />}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 flex flex-col min-h-[200px]">
                  <div className="px-3 border-b border-slate-800 h-14 shrink-0 flex flex-col justify-center">
                    <p className="text-sm font-medium text-slate-300 leading-tight">ReHub bright-2</p>
                    <p className="text-[11px] text-slate-500 leading-tight">sandbox candidate</p>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                    {running ? <Loader2 size={22} className="text-brand-400 mb-2 animate-spin" /> : <Cpu size={22} className="text-slate-600 mb-2" />}
                    <p className="text-xs text-slate-500">{running ? 'Finishing the same merge with bright-2…' : 'No bright-2 image for this run.'}</p>
                  </div>
                </div>
              )
            )}
            {geminiRequested && (
              geminiUrl ? (
                <CompareCard title="ReHub + Gemini" subtitle={replaceSky ? 'colour + blue sky' : 'generative colour pass'} url={geminiUrl} dims={null} footer={<ProviderFooter meta={detail?.job.sandbox_provider_meta?.gemini} />} />
              ) : (
                <div className="rounded-2xl border border-dashed border-fuchsia-500/30 bg-fuchsia-500/5 p-5 flex flex-col items-center justify-center text-center min-h-[200px]">
                  {running ? <Loader2 size={22} className="text-fuchsia-400 mb-2 animate-spin" /> : <Sparkles size={22} className="text-fuchsia-400/60 mb-2" />}
                  <p className="text-sm font-medium text-slate-300">ReHub + Gemini</p>
                  <p className="text-xs text-slate-500 mt-1">{running ? 'Running Gemini pass on the ReHub render…' : 'No Gemini image (key missing or model declined).'}</p>
                </div>
              )
            )}
            {openaiRequested && (
              openaiUrl ? (
                <CompareCard title="ReHub + OpenAI" subtitle="gpt-image-2.5 · photographic finish" url={openaiUrl} dims={null} footer={<ProviderFooter meta={detail?.job.sandbox_provider_meta?.openai} />} />
              ) : (
                <div className="rounded-2xl border border-dashed border-emerald-500/30 bg-emerald-500/5 p-5 flex flex-col items-center justify-center text-center min-h-[200px]">
                  {running ? <Loader2 size={22} className="text-emerald-400 mb-2 animate-spin" /> : <Sparkles size={22} className="text-emerald-400/60 mb-2" />}
                  <p className="text-sm font-medium text-slate-300">ReHub + OpenAI</p>
                  <p className="text-xs text-slate-500 mt-1">{running ? 'Running gpt-image-2.5 on the ReHub render…' : 'No OpenAI image (key missing, org unverified, or model declined).'}</p>
                </div>
              )
            )}
            {falRequested && (
              falUrl ? (
                <CompareCard title={`ReHub + ${falOption.label}`} subtitle={`fal · ${falOption.price}${falOption.prompted && replaceSky ? ' · colour + sky' : ''}`} url={falUrl} dims={null} />
              ) : (
                <div className="rounded-2xl border border-dashed border-violet-500/30 bg-violet-500/5 p-5 flex flex-col items-center justify-center text-center min-h-[200px]">
                  {running ? <Loader2 size={22} className="text-violet-400 mb-2 animate-spin" /> : <Sparkles size={22} className="text-violet-400/60 mb-2" />}
                  <p className="text-sm font-medium text-slate-300">ReHub + {falOption.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{running ? `Running ${falOption.label} on the ReHub render…` : 'No fal image (key missing or model declined).'}</p>
                </div>
              )
            )}
            <CompareCard
              title={PROVIDER_LABELS[detail.source?.provider || 'unknown'] || detail.source?.provider || 'Historical'}
              subtitle="historical output"
              url={historicalUrl}
              dims={detail.source?.output_width && detail.source?.output_height ? `${detail.source.output_width}×${detail.source.output_height}` : null}
            />
          </div>

          {(candidateUrl || bright2Url) && jobId && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 px-4 py-3 space-y-2">
              <p className="text-xs text-slate-400">
                Scene <span className="font-mono text-slate-300">{detail?.job.media_item_id}</span>
                {bright2Meta?.checks?.wall_l != null && (
                  <> · bright-2 wall L <span className="font-mono text-slate-300">{bright2Meta.checks.wall_l}</span></>
                )}
                {listed['bright-1']?.checks?.wall_l != null && (
                  <> · bright-1 wall L <span className="font-mono text-slate-300">{listed['bright-1'].checks.wall_l}</span></>
                )}
                {listed.baseline?.checks?.wall_l != null && (
                  <> · baseline wall L <span className="font-mono text-slate-300">{listed.baseline.checks.wall_l}</span></>
                )}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-slate-500">Which looks better?</span>
                {([
                  ['baseline', 'Baseline'],
                  ['bright', 'Bright-1'],
                  ['bright-2', 'Bright-2'],
                  ['historical', 'Historical'],
                  ['tie', 'Tie'],
                ] as const).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    disabled={preferring}
                    onClick={() => savePreference(id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-60 ${detail?.job.candidate_preference === id ? 'bg-brand-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                  >
                    {label}
                  </button>
                ))}
                {detail?.job.candidate_preference && (
                  <span className="text-[11px] text-slate-500">Saved on this job.</span>
                )}
              </div>
            </div>
          )}

          {/* QualityNet verdict + decision + cost/timing */}
          {detail.run && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2"><ShieldCheck size={15} className="text-brand-400" /> Engine decision</h3>
                {action && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${ACTION_LABELS[action]?.style || 'bg-slate-800 text-slate-300'}`}>
                    {ACTION_LABELS[action]?.label || action}
                  </span>
                )}
                {verdict && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${QUALITY_STYLES[verdict] || 'bg-slate-800 text-slate-300'}`}>
                    QualityNet: {verdict}{typeof quality?.score === 'number' ? ` · ${quality.score.toFixed(2)}` : ''}
                  </span>
                )}
                <p className="text-[11px] text-slate-500 basis-full">
                  The score mixes sharpness, clipped highlights, clipped shadows and white balance. A pass means those checks cleared. It is not a vote for the better photograph. Mean brightness passes anywhere from 40 to 220, so a dull interior can still pass.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Delivered source" value={PROVIDER_LABELS[detail.run.delivered_source || 'none'] || detail.run.delivered_source || '—'} />
                <Stat label="Est. cost" value={`$${(detail.run.est_cost_usd || 0).toFixed(3)}`} sub={`vs $${AE_BASELINE_USD.toFixed(2)}/img external`} />
                <Stat label="Processing time" value={detail.run.duration_ms ? `${(detail.run.duration_ms / 1000).toFixed(1)}s` : '—'} />
                <Stat label="Providers called" value={(detail.run.providers_called || []).map((p) => PROVIDER_LABELS[p] || p).join(', ') || 'none'} />
              </div>

              {metrics && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">QualityNet metrics</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {['sharpness', 'mean_luma', 'clipped_high', 'clipped_low', 'wb_deviation'].map((k) =>
                      typeof metrics[k] === 'number' ? (
                        <div key={k} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                          <p className="text-[11px] text-slate-500">{k.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-slate-200 font-mono">{metrics[k].toFixed(3)}</p>
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>
              )}

              {detail.run.model_versions && Object.keys(detail.run.model_versions).length > 0 && (
                <p className="text-[11px] text-slate-500">
                  Models: {Object.entries(detail.run.model_versions).map(([k, v]) => `${k}=${v ?? '—'}`).join(' · ')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!jobId && (
        <EmptyBox>Pick a validated training pair and press “Run in sandbox”. The engine worker processes it on the isolated queue and the result streams back here for a three-way comparison.</EmptyBox>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROCESSING CONFIGURATION — engine mode + provider order (read-only; Phase 10)
// ─────────────────────────────────────────────────────────────────────────────
const MODES: { id: string; label: string; hint: string }[] = [
  { id: 'external_only', label: 'External only', hint: 'All jobs go to the locked external provider (AE/AP). The engine does not process production jobs.' },
  { id: 'shadow', label: 'Shadow', hint: 'Engine processes a copy alongside the external provider for comparison; external output is still delivered.' },
  { id: 'hybrid', label: 'Hybrid', hint: 'Engine delivers when confident; routes to a fallback provider otherwise.' },
  { id: 'internal_testing', label: 'Internal testing', hint: 'Engine processes selected media on the isolated queue; nothing is delivered to users.' },
];

const ProcessingConfigPanel: React.FC = () => {
  const [pipeline, setPipeline] = useState<HdrPipelineSetting | null>(null);
  const [rollout, setRollout] = useState<HdrRolloutState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // draft form state
  const [mode, setMode] = useState<HdrRolloutMode>('external_only');
  const [enabled, setEnabled] = useState(false);
  const [canary, setCanary] = useState(0);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    const [p, r] = await Promise.all([getHdrAdminPipeline(), getHdrRollout()]);
    if (p.success && p.setting) setPipeline(p.setting);
    if (!r.success) setError(r.error || 'Failed to load rollout state');
    else if (r.state) {
      setRollout(r.state);
      setMode(r.state.config.mode);
      setEnabled(r.state.config.enabled);
      setCanary(r.state.config.canary_percent || 0);
      setNote('');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cfg = rollout?.config;
  const requiresApproval = enabled && mode !== 'external_only';
  const dirty = !!cfg && (mode !== cfg.mode || enabled !== cfg.enabled || canary !== (cfg.canary_percent || 0));

  const save = async () => {
    if (requiresApproval && !note.trim()) {
      setError('An approval note is required to enable a non–external-only mode.');
      return;
    }
    if (requiresApproval && !window.confirm(
      `Deploy new processing behaviour?\n\nMode: ${mode}${mode === 'hybrid' ? ` (${canary}% canary)` : ''}\n\n` +
      `This lets the internal engine participate in processing. The live provider lock is unchanged and you can roll back instantly.`
    )) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    const res = await setHdrRollout({ mode, enabled, canaryPercent: canary, note: note.trim() || null });
    setSaving(false);
    if (!res.success) { setError(res.error === 'APPROVAL_NOTE_REQUIRED' ? 'An approval note is required to enable a non–external-only mode.' : (res.error || 'Save failed')); return; }
    if (res.state) { setRollout(res.state); setNote(''); }
    setMessage('Rollout updated. The production provider lock was not changed.');
  };

  const doRollback = async () => {
    if (!window.confirm('Instantly roll back to external-only (AE/AP)?\n\nThe internal engine stops participating immediately.')) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    const res = await rollbackHdrRollout('manual rollback from dashboard');
    setSaving(false);
    if (!res.success) { setError(res.error || 'Rollback failed'); return; }
    if (res.state) {
      setRollout(res.state);
      setMode(res.state.config.mode);
      setEnabled(res.state.config.enabled);
      setCanary(res.state.config.canary_percent || 0);
      setNote('');
    }
    setMessage('Rolled back to external-only. AE/AP delivers all production jobs.');
  };

  const activeIsExternal = !cfg?.enabled || cfg?.mode === 'external_only';

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Processing configuration & rollout"
        subtitle="Feature-flagged rollout of the internal engine (external-only → shadow → hybrid → internal-testing). This never changes the live provider lock, and rollback is instant."
        onRefresh={load}
        loading={loading}
      />
      {error && <ErrorBox error={error} />}
      {message && <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">{message}</div>}

      {/* Current state banner */}
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${activeIsExternal ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-amber-500/30 bg-amber-500/10 text-amber-200'}`}>
        {activeIsExternal ? <ShieldCheck size={18} className="shrink-0 mt-0.5" /> : <AlertTriangle size={18} className="shrink-0 mt-0.5" />}
        <div>
          <p className="font-medium">
            {activeIsExternal
              ? 'External-only — production unchanged.'
              : `Engine active: ${MODES.find((m) => m.id === cfg?.mode)?.label || cfg?.mode}${cfg?.mode === 'hybrid' ? ` (${cfg?.canary_percent}% canary)` : ''}.`}
          </p>
          <p className="text-xs opacity-80 mt-0.5">
            {activeIsExternal
              ? 'The internal engine does not touch production delivery. Backward compatible with the existing AE/AP integration.'
              : 'The internal engine is participating in processing under an approved rollout. Live provider lock remains the fallback; roll back any time.'}
            {cfg?.approval_note && <> Approval: “{cfg.approval_note}”.</>}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Live production provider (read-only) */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
          <h3 className="text-sm font-semibold text-white mb-1">Live production provider</h3>
          <p className="text-xs text-slate-500 mb-3">The admin lock managed on the <span className="text-slate-300">Processing</span> tab. Read-only here; the rollout layer never overwrites it.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500/15 border border-brand-500/40 text-sm text-white">
              <Lock size={12} className="text-brand-300" />
              {loading ? 'Loading…' : (rollout?.live_provider ? (PROVIDER_LABELS[rollout.live_provider] || rollout.live_provider) : (pipeline?.pipeline ? (PROVIDER_LABELS[pipeline.pipeline] || pipeline.pipeline) : 'Unknown'))}
            </span>
            {pipeline?.updated_at && <span className="text-[11px] text-slate-600">locked {new Date(pipeline.updated_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Instant rollback */}
        <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2"><RotateCcw size={15} className="text-red-400" /> Instant rollback</h3>
            <p className="text-xs text-slate-400">Immediately return to external-only so AE/AP delivers every job. Always available; no approval required.</p>
          </div>
          <button
            onClick={doRollback}
            disabled={saving || activeIsExternal}
            className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            title={activeIsExternal ? 'Already external-only' : 'Roll back to external-only'}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
            Roll back to AE/AP
          </button>
        </div>
      </div>

      {/* Rollout control */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Power size={15} className={enabled ? 'text-emerald-400' : 'text-slate-500'} /> Rollout mode</h3>
          <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-brand-500" />
            Feature flag {enabled ? 'on' : 'off'}
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {MODES.map((m) => {
            const selected = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id as HdrRolloutMode)}
                className={`text-left rounded-xl border px-4 py-3 transition-colors ${selected ? 'border-brand-500/70 bg-brand-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-white">{m.label}</p>
                  {selected && <CheckCircle2 size={15} className="text-brand-400" />}
                </div>
                <p className="text-xs text-slate-400 mt-1">{m.hint}</p>
              </button>
            );
          })}
        </div>

        {mode === 'hybrid' && (
          <div>
            <label className="text-xs text-slate-400 flex items-center justify-between">
              <span>Canary percentage (engine delivers this % of items; rest go to {cfg?.fallback_provider || 'autoenhance'})</span>
              <span className="text-white font-medium">{canary}%</span>
            </label>
            <input type="range" min={0} max={100} step={5} value={canary} onChange={(e) => setCanary(Number(e.target.value))} className="w-full mt-2 accent-brand-500" />
          </div>
        )}

        {requiresApproval && (
          <div>
            <label className="text-xs text-slate-400">Approval note (required to enable a non–external-only mode)</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Approved by Ben — 10% canary after benchmark review"
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 px-3 py-2"
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          <p className="text-[11px] text-slate-600">
            Fallback order when the engine declines: <code>autoenhance → autopix</code>. The live provider lock is never changed by this screen.
          </p>
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <SlidersHorizontal size={16} />}
            Save rollout
          </button>
        </div>
      </div>

      {/* Audit trail */}
      {rollout && rollout.events.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800"><h3 className="text-sm font-semibold text-white">Rollout history</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                  <th className="px-3 py-2.5 font-medium">Event</th>
                  <th className="px-3 py-2.5 font-medium">Change</th>
                  <th className="px-3 py-2.5 font-medium">Flag</th>
                  <th className="px-3 py-2.5 font-medium">Note</th>
                  <th className="px-3 py-2.5 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {rollout.events.map((ev) => (
                  <tr key={ev.id} className="border-b border-slate-800/80">
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ev.event === 'rollback' ? 'bg-red-500/20 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{ev.event}</span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{ev.from_mode || '—'} → {ev.to_mode || '—'}{ev.event === 'set' && ev.to_mode === 'hybrid' ? ` (${ev.canary_percent}%)` : ''}</td>
                    <td className="px-3 py-2.5 text-slate-500">{ev.enabled ? 'on' : 'off'}</td>
                    <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[220px] truncate" title={ev.note || ''}>{ev.note || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{new Date(ev.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TRAINING DATASETS — Phase 2 inventory / validation / rights (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const pageSize = 50;

const TrainingDatasetsPanel: React.FC = () => {
  const [overview, setOverview] = useState<HdrDatasetOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [pairs, setPairs] = useState<HdrTrainingPair[]>([]);
  const [pairsTotal, setPairsTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [providerFilter, setProviderFilter] = useState('');
  const [validationFilter, setValidationFilter] = useState('');
  const [rightsFilter, setRightsFilter] = useState('');

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    const res = await getHdrDatasetOverview();
    if (!res.success) setError(res.error || 'Failed to load dataset overview');
    else setOverview(res.overview || null);
    setLoading(false);
  };

  const loadPairs = async (nextPage = page) => {
    const res = await listHdrTrainingPairs({
      limit: pageSize,
      offset: nextPage * pageSize,
      provider: providerFilter || null,
      validation: validationFilter || null,
      rights: rightsFilter || null,
    });
    if (res.success) {
      setPairs(res.pairs || []);
      setPairsTotal(res.total || 0);
    } else {
      setError(res.error || 'Failed to load pairs');
    }
  };

  useEffect(() => {
    loadOverview();
    loadPairs(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(0);
    loadPairs(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter, validationFilter, rightsFilter]);

  const runSync = async () => {
    setSyncing(true);
    setMessage(null);
    setError(null);
    const res = await syncHdrTrainingPairs();
    setSyncing(false);
    if (!res.success) {
      setError(res.error || 'Sync failed');
      return;
    }
    setMessage(`Inventory synced — ${res.upserted ?? 0} pairs refreshed. Run pixel validation from the engine CLI to grade quality.`);
    await Promise.all([loadOverview(), loadPairs(page)]);
  };

  const approveProvider = async (provider: string) => {
    const label = PROVIDER_LABELS[provider] || provider;
    if (!window.confirm(
      `Approve ALL ${label} pairs for training use?\n\n` +
      `Only do this once you have verified rights to train on ${label} outputs.`
    )) return;
    const res = await setHdrPairRights({ rights: 'approved', provider, notes: `Bulk approved via admin (${provider})` });
    if (!res.success) { setError(res.error || 'Rights update failed'); return; }
    setMessage(`Approved ${res.updated ?? 0} ${label} pairs for training.`);
    await Promise.all([loadOverview(), loadPairs(page)]);
  };

  const setRights = async (pairId: string, rights: 'approved' | 'restricted' | 'needs_review') => {
    const res = await setHdrPairRights({ rights, pairId });
    if (!res.success) { setError(res.error || 'Rights update failed'); return; }
    await Promise.all([loadPairs(page), loadOverview()]);
  };

  const inv = overview?.inventory;
  const providerEntries = overview ? Object.entries(overview.by_provider) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-400 max-w-2xl">
            Historical training dataset built from the existing HDRPhoto archive. Pairs reference
            existing storage objects (no bytes duplicated). Sync refreshes metadata; pixel validation
            and manifest export run from the <code className="text-slate-300">rehub-hdr-engine</code> CLI.
          </p>
          {overview?.synced_at && (
            <p className="text-[11px] text-slate-600 mt-1">Last sync {new Date(overview.synced_at).toLocaleString()}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium disabled:opacity-60"
          >
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />}
            Sync inventory
          </button>
          <button
            onClick={() => { loadOverview(); loadPairs(page); }}
            disabled={loading}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
          {(error.includes('function') || error.includes('does not exist')) && (
            <span className="block mt-1 text-red-400/80 text-xs">
              Apply migration <code>20261013090000_hdr_engine_training_datasets.sql</code> to enable the HDR Engine dashboard.
            </span>
          )}
        </div>
      )}
      {message && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">{message}</div>
      )}

      {loading && !overview ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand-500 animate-spin" /></div>
      ) : overview ? (
        <>
          {/* Inventory stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total HDR jobs" value={inv?.total_jobs ?? 0} />
            <Stat label="Complete brackets (3–5)" value={inv?.complete_brackets ?? 0} />
            <Stat label="With finished output" value={inv?.with_output ?? 0} />
            <Stat label="Missing / invalid" value={inv?.missing_or_invalid ?? 0} accent="text-amber-300" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Available pairs (valid + approved)" value={inv?.available_pairs ?? 0} accent="text-emerald-300" icon={<CheckCircle2 size={18} className="text-emerald-400" />} />
            <Stat label="Valid, pending rights" value={inv?.valid_pending_rights ?? 0} accent="text-amber-300" icon={<ShieldCheck size={18} className="text-amber-400" />} />
            <Stat label="Pending pixel validation" value={inv?.pending_pixel_validation ?? 0} />
            <Stat label="Datasets" value={overview.datasets.length} />
          </div>

          {/* Provider breakdown */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">By provider (AE vs AP vs internal)</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {providerEntries.length === 0 && <p className="text-sm text-slate-500">No pairs yet — run Sync inventory.</p>}
              {providerEntries.map(([provider, stat]) => (
                <div key={provider} className="rounded-xl bg-slate-950 border border-slate-800 p-4">
                  <p className="text-xs text-slate-400">{PROVIDER_LABELS[provider] || provider}</p>
                  <p className="text-lg font-semibold text-white mt-1">{stat.total} pairs</p>
                  <p className="text-xs text-slate-500 mt-1">{stat.valid} valid · {stat.approved} approved</p>
                  {(provider === 'autoenhance' || provider === 'autopix') && stat.valid > stat.approved && (
                    <button
                      onClick={() => approveProvider(provider)}
                      className="mt-2 text-xs px-2 py-1 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white"
                    >
                      Approve rights ({stat.valid - stat.approved})
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-600 mt-3 flex items-center gap-1.5">
              <AlertTriangle size={12} className="text-amber-400" />
              Provider (AE/AP) outputs require verified rights before training use. Internal results need no rights review.
            </p>
          </div>

          {/* Validation + quality distribution + active dataset */}
          <div className="grid md:grid-cols-3 gap-3">
            <DistCard title="Validation status" dist={overview.validation} styles={VALIDATION_STYLES} />
            <DistCard title="Quality distribution" dist={overview.quality_distribution} />
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Active dataset split</h3>
              {overview.active_dataset ? (
                <div className="space-y-1 text-sm">
                  <p className="text-white font-medium">{overview.active_dataset.version}{overview.active_dataset.name ? ` · ${overview.active_dataset.name}` : ''}</p>
                  <p className="text-slate-400">Train <span className="text-white">{overview.active_dataset.train}</span></p>
                  <p className="text-slate-400">Validation <span className="text-white">{overview.active_dataset.validation}</span></p>
                  <p className="text-slate-400">Test <span className="text-white">{overview.active_dataset.test}</span></p>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No ready dataset yet. Create one with the engine CLI (<code>build-manifest</code>) once pairs are approved.</p>
              )}
            </div>
          </div>

          {/* Pair table */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap items-center gap-2 justify-between">
              <h3 className="text-sm font-semibold text-white">Training pairs</h3>
              <div className="flex flex-wrap gap-2">
                <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
                  <option value="">All providers</option>
                  <option value="autoenhance">Autoenhance</option>
                  <option value="autopix">Autopix</option>
                  <option value="gemini">Gemini</option>
                  <option value="fal">fal</option>
                </select>
                <select value={validationFilter} onChange={(e) => setValidationFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
                  <option value="">All validation</option>
                  <option value="valid">Valid</option>
                  <option value="pending">Pending</option>
                  <option value="quarantined">Quarantined</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select value={rightsFilter} onChange={(e) => setRightsFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 px-2 py-1.5">
                  <option value="">All rights</option>
                  <option value="approved">Approved</option>
                  <option value="needs_review">Needs review</option>
                  <option value="restricted">Restricted</option>
                </select>
              </div>
            </div>
            {pairs.length === 0 ? (
              <p className="text-sm text-slate-500 p-8 text-center">No pairs in this view</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                      <th className="px-3 py-2.5 font-medium">Listing</th>
                      <th className="px-3 py-2.5 font-medium">Provider</th>
                      <th className="px-3 py-2.5 font-medium">Brackets</th>
                      <th className="px-3 py-2.5 font-medium">Output</th>
                      <th className="px-3 py-2.5 font-medium">Validation</th>
                      <th className="px-3 py-2.5 font-medium">Rights</th>
                      <th className="px-3 py-2.5 font-medium">Reasons</th>
                      <th className="px-3 py-2.5 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.map((p) => {
                      const reasons = [...(p.meta_reasons || []), ...(p.pixel_reasons || [])];
                      return (
                        <tr key={p.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                          <td className="px-3 py-2.5 text-slate-300 truncate max-w-[160px]" title={p.listing_name || ''}>{p.listing_name || '—'}</td>
                          <td className="px-3 py-2.5 text-slate-400">{p.provider}</td>
                          <td className="px-3 py-2.5 text-slate-400">{p.source_count}{p.media_type === 'single' ? ' (single)' : ''}</td>
                          <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{p.output_width && p.output_height ? `${p.output_width}×${p.output_height}` : '—'}</td>
                          <td className="px-3 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${VALIDATION_STYLES[p.validation_status] || 'bg-slate-700 text-slate-300'}`}>{p.validation_status}</span></td>
                          <td className="px-3 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${RIGHTS_STYLES[p.rights_status] || 'bg-slate-700 text-slate-300'}`}>{p.rights_status}</span></td>
                          <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[180px] truncate" title={reasons.join(', ')}>{reasons.join(', ') || '—'}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex gap-1">
                              {p.rights_status !== 'approved' && (
                                <button onClick={() => setRights(p.id, 'approved')} className="text-[11px] px-2 py-1 rounded bg-emerald-600/70 hover:bg-emerald-600 text-white">Approve</button>
                              )}
                              {p.rights_status !== 'restricted' && (
                                <button onClick={() => setRights(p.id, 'restricted')} className="text-[11px] px-2 py-1 rounded bg-slate-700 hover:bg-red-600/70 text-slate-200">Restrict</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {pairsTotal > pageSize && (
              <div className="flex items-center justify-between p-3 border-t border-slate-800">
                <p className="text-xs text-slate-500">Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, pairsTotal)} of {pairsTotal}</p>
                <div className="flex gap-2">
                  <button disabled={page === 0} onClick={() => { const n = page - 1; setPage(n); loadPairs(n); }} className="px-3 py-1.5 rounded-lg bg-slate-800 text-sm text-slate-300 disabled:opacity-40">Previous</button>
                  <button disabled={(page + 1) * pageSize >= pairsTotal} onClick={() => { const n = page + 1; setPage(n); loadPairs(n); }} className="px-3 py-1.5 rounded-lg bg-slate-800 text-sm text-slate-300 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-16 text-slate-500 text-sm">No dataset data. Click “Sync inventory” to build it from the archive.</div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BENCHMARKS — fixed-dataset metrics + blind ReHub-vs-historical comparison
// ─────────────────────────────────────────────────────────────────────────────
const BenchmarksPanel: React.FC = () => {
  const [overview, setOverview] = useState<HdrBenchmarkOverview | null>(null);
  const [results, setResults] = useState<HdrBenchmarkResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [o, r] = await Promise.all([getHdrBenchmarkOverview(20), getHdrBenchmarkResults(null)]);
    if (!o.success) setError(o.error || 'Failed to load benchmark overview');
    else setOverview(o.overview || null);
    if (r.success) setResults(r.results || null);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const latest = overview?.latest || null;
  const runId = latest?.id || null;
  const m = latest?.metrics;

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Benchmarks"
        subtitle="Objective metrics over a fixed historical test split plus a blind review where ReHub is compared against the historical provider without revealing which is which. Metrics are measurements — the engine is not treated as better until the blind results demonstrate it."
        onRefresh={load}
        loading={loading}
      />
      {error && <ErrorBox error={error} />}

      {loading && !overview ? (
        <Spinner />
      ) : !latest ? (
        <EmptyBox>
          No benchmark runs yet. Produce one with
          {' '}<code>rehub-hdr-engine benchmark --manifest &lt;manifest&gt; --split test --register</code>.
          The <code>--register</code> flag uploads the ReHub results and populates the blind-review queue below.
        </EmptyBox>
      ) : (
        <>
          {/* Latest run — objective metrics */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-white">{latest.label}</h3>
                <p className="text-[11px] text-slate-500">
                  split {latest.split || '—'} · {latest.item_count} images · {latest.difficult_count} difficult ·{' '}
                  {new Date(latest.created_at).toLocaleString()}
                </p>
              </div>
              <GpuBadge gpu={m?.gpu} peak={m?.peak_rss_mb ?? null} />
            </div>

            <BenchmarkMetricGrid title="Overall" agg={m?.overall} />
            {m?.difficult_scenes && (m.difficult_scenes.count || 0) > 0 && (
              <>
                <BenchmarkMetricGrid title="Difficult scenes" agg={m.difficult_scenes} />
                {m.difficult_scenes.by_tag && Object.keys(m.difficult_scenes.by_tag).length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {Object.entries(m.difficult_scenes.by_tag).map(([tag, n]) => (
                      <span key={tag} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] bg-slate-800 text-slate-300">
                        {tag.replace(/_/g, ' ')}: {n}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {m?.by_provider && Object.keys(m.by_provider).length > 0 && (
              <div className="pt-1">
                <p className="text-xs text-slate-500 mb-2">By historical provider (objective quality vs that provider's own output)</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {Object.entries(m.by_provider).map(([prov, agg]) => (
                    <div key={prov} className="rounded-xl bg-slate-950 border border-slate-800 p-3">
                      <p className="text-xs text-slate-300">{PROVIDER_LABELS[prov] || prov}</p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {agg.count} imgs · PSNR {fmt(agg.psnr_mean)} · SSIM {fmt(agg.ssim_mean)} · arch {fmt(agg.architectural_mean)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Blind review */}
          <BlindReview runId={runId} onVoted={() => getHdrBenchmarkResults(runId).then((r) => r.success && setResults(r.results || null))} />

          {/* Human preference results */}
          <HumanPreference results={results} />
        </>
      )}
    </div>
  );
};

function fmt(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : Number(v).toFixed(v < 1 && v > 0 ? 3 : 1);
}

function GpuBadge({ gpu, peak }: { gpu?: { available: boolean; device: string }; peak: number | null }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${gpu?.available ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}>
        <Cpu size={12} /> {gpu?.available ? gpu.device : 'CPU only'}
      </span>
      {peak != null && <span className="inline-flex px-2.5 py-1 rounded-lg bg-slate-800 text-slate-400">peak mem {peak} MB</span>}
    </div>
  );
}

function BenchmarkMetricGrid({ title, agg }: { title: string; agg?: HdrBenchmarkAggregate }) {
  if (!agg || !agg.count) return null;
  const accept = agg.internal_acceptance_rate != null ? Math.round(agg.internal_acceptance_rate * 100) : null;
  const fallback = agg.fallback_frequency != null ? Math.round(agg.fallback_frequency * 100) : null;
  const quality = agg.quality_pass_rate != null ? Math.round(agg.quality_pass_rate * 100) : null;
  return (
    <div>
      <p className="text-xs text-slate-500 mb-2">{title} · {agg.count} images</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="PSNR (mean)" value={fmt(agg.psnr_mean)} sub="dB vs historical" />
        <Stat label="SSIM (mean)" value={fmt(agg.ssim_mean)} sub="structural" />
        <Stat label="Architectural" value={fmt(agg.architectural_mean)} sub="edge-structure proxy" />
        <Stat label="Quality pass" value={quality === null ? '—' : `${quality}%`} sub="QualityNet verdict" />
        <Stat label="Internal acceptance" value={accept === null ? '—' : `${accept}%`} accent="text-emerald-300" sub="would deliver internally" />
        <Stat label="Fallback frequency" value={fallback === null ? '—' : `${fallback}%`} accent="text-sky-300" sub="would route to AE/AP" />
        <Stat label="Avg speed" value={agg.speed_ms_mean != null ? `${(agg.speed_ms_mean / 1000).toFixed(2)}s` : '—'} sub={agg.cpu_ms_mean != null ? `${(agg.cpu_ms_mean / 1000).toFixed(2)}s CPU` : undefined} />
        <Stat label="Est. savings" value={agg.estimated_savings_usd != null ? `$${agg.estimated_savings_usd.toFixed(2)}` : '—'} accent="text-emerald-300" sub={agg.baseline_cost_usd != null ? `vs $${agg.baseline_cost_usd.toFixed(2)} external` : undefined} />
      </div>
    </div>
  );
}

const BlindReview: React.FC<{ runId: string | null; onVoted: () => void }> = ({ runId, onVoted }) => {
  const [next, setNext] = useState<HdrBenchmarkNext | null>(null);
  const [loading, setLoading] = useState(false);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNext = async () => {
    setLoading(true);
    setError(null);
    const res = await getHdrBenchmarkNext(runId);
    if (!res.success) setError(res.error || 'Failed to load next comparison');
    else setNext(res.next || null);
    setLoading(false);
  };
  useEffect(() => { fetchNext(); /* eslint-disable-next-line */ }, [runId]);

  const vote = async (slot: 'a' | 'b' | 'tie' | 'skip') => {
    if (!next?.comparison_id || next.a_is_rehub === undefined) return;
    setVoting(true);
    const res = await submitHdrBenchmarkVote({ comparisonId: next.comparison_id, slot, aIsRehub: next.a_is_rehub });
    setVoting(false);
    if (!res.success) { setError(res.error || 'Vote failed'); return; }
    onVoted();
    await fetchNext();
  };

  const slotAUrl = hdrStorageUrl(next?.slot_a);
  const slotBUrl = hdrStorageUrl(next?.slot_b);

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Scale size={15} className="text-brand-400" /> Blind comparison</h3>
        {next && !next.done && <span className="text-[11px] text-slate-500">{next.remaining} left to review</span>}
      </div>
      <p className="text-[11px] text-slate-600">
        You are shown two results for the same property with the provider hidden and the sides randomised each time. Pick the
        better photograph — the mapping to ReHub / historical is recorded only on the server.
      </p>
      {error && <ErrorBox error={error} />}

      {loading && !next ? (
        <Spinner />
      ) : !next || next.done ? (
        <EmptyBox>
          {next && next.remaining === 0 && (next as any).run_id
            ? 'All comparisons in this run have been reviewed. Thank you — see the aggregate results below.'
            : 'Nothing to review yet. Register a benchmark run to populate the blind queue.'}
        </EmptyBox>
      ) : (
        <>
          {next.is_difficult && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] bg-amber-500/15 text-amber-300">
                <AlertTriangle size={12} /> difficult scene
              </span>
              {(next.scene_tags || []).map((t) => (
                <span key={t} className="inline-flex px-2 py-0.5 rounded-lg text-[11px] bg-slate-800 text-slate-400">{t.replace(/_/g, ' ')}</span>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <BlindSlot label="A" url={slotAUrl} onPick={() => vote('a')} disabled={voting} />
            <BlindSlot label="B" url={slotBUrl} onPick={() => vote('b')} disabled={voting} />
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button onClick={() => vote('a')} disabled={voting} className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium disabled:opacity-60">A is better</button>
            <button onClick={() => vote('tie')} disabled={voting} className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-60">Tie</button>
            <button onClick={() => vote('b')} disabled={voting} className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium disabled:opacity-60">B is better</button>
            <button onClick={() => vote('skip')} disabled={voting} className="px-4 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-800 text-slate-400 text-sm disabled:opacity-60">Skip</button>
          </div>
        </>
      )}
    </div>
  );
};

function BlindSlot({ label, url, onPick, disabled }: { label: string; url: string | null; onPick: () => void; disabled: boolean }) {
  return (
    <button onClick={onPick} disabled={disabled} className="group relative rounded-2xl overflow-hidden border border-slate-800 bg-black disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-brand-500">
      {url ? (
        <img src={getThumbnailUrl(url, { width: 1200, height: 1200, quality: 74, resize: 'contain' }) || url} alt={`option ${label}`} className="w-full aspect-[4/3] object-contain" loading="lazy" />
      ) : (
        <div className="aspect-[4/3] flex items-center justify-center text-sm text-slate-600">No image</div>
      )}
      <span className="absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded bg-black/70 text-white">{label}</span>
      <span className="absolute inset-0 bg-brand-500/0 group-hover:bg-brand-500/10 transition-colors" />
    </button>
  );
}

const HumanPreference: React.FC<{ results: HdrBenchmarkResults | null }> = ({ results }) => {
  if (!results || !results.overall || !results.overall.votes) {
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Trophy size={15} className="text-brand-400" /> Human preference</h3>
        <p className="text-sm text-slate-500">No votes recorded yet. Results appear here as reviewers complete the blind comparison above.</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-4">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Trophy size={15} className="text-brand-400" /> Human preference</h3>
      <PreferenceRow label="Overall" pref={results.overall} />
      {results.by_difficulty && Object.keys(results.by_difficulty).length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">By scene difficulty</p>
          {Object.entries(results.by_difficulty).map(([bucket, pref]) => (
            <PreferenceRow key={bucket} label={bucket === 'difficult' ? 'Difficult scenes' : 'Standard scenes'} pref={pref} />
          ))}
        </div>
      )}
      {results.by_provider && Object.keys(results.by_provider).length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">By historical provider</p>
          {Object.entries(results.by_provider).map(([prov, pref]) => (
            <PreferenceRow key={prov} label={PROVIDER_LABELS[prov] || prov} pref={pref} />
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-600">
        ReHub win rate = ReHub preferred ÷ (ReHub + historical) decisive votes; ties and skips are excluded from the rate.
      </p>
    </div>
  );
};

function PreferenceRow({ label, pref }: { label: string; pref: HdrBenchmarkPreference }) {
  const rate = pref.rehub_win_rate;
  const pct = rate == null ? null : Math.round(rate * 100);
  const barColor = pct == null ? 'bg-slate-600' : pct >= 50 ? 'bg-emerald-500' : 'bg-sky-500';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-300">{label}</span>
        <span className="text-white font-medium">{pct == null ? '—' : `${pct}% ReHub`}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${pct ?? 0}%` }} />
      </div>
      <p className="text-[11px] text-slate-600">
        {pref.votes} votes · ReHub {pref.rehub} · historical {pref.reference} · tie {pref.tie}{pref.skip != null ? ` · skip ${pref.skip}` : ''}
      </p>
    </div>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────
function PanelHeader({ title, subtitle, onRefresh, loading }: { title: string; subtitle: string; onRefresh: () => void; loading: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <p className="text-sm text-slate-400 max-w-2xl mt-0.5">{subtitle}</p>
      </div>
      <button onClick={onRefresh} disabled={loading} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50" title="Refresh">
        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
      </button>
    </div>
  );
}

function Spinner() {
  return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand-500 animate-spin" /></div>;
}

function ErrorBox({ error }: { error: string }) {
  return <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>;
}

function EmptyBox({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-6 text-sm text-slate-500 text-center">{children}</div>;
}

function Stat({ label, value, accent = 'text-white', icon, sub }: { label: string; value: number | string; accent?: string; icon?: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${accent}`}>{value}</p>
          {sub && <p className="text-[11px] text-slate-600 mt-0.5">{sub}</p>}
        </div>
        {icon}
      </div>
    </div>
  );
}

function DistCard({ title, dist, styles }: { title: string; dist: Record<string, number>; styles?: Record<string, string> }) {
  const entries = Object.entries(dist || {});
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">No data</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${styles?.[k] || 'bg-slate-800 text-slate-300'}`}>{k}</span>
              <span className="text-white font-medium">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HdrEngineAdmin;
